import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import AnnotationOverlay, { type Annotation } from './AnnotationOverlay';
import AnnotationList from './AnnotationList';

// UXV.V2 (#1417) — governance-grade screen preview card.
//
// Fetches the V1-rendered HTML for the selected screen from the backend render
// endpoint and displays it in a SANDBOXED iframe via `srcDoc`.
//
// SECURITY (H1 — the headline finding): the iframe uses `sandbox=""` — empty,
// ALL restrictions, zero flags. It NEVER carries `allow-scripts` and NEVER
// `allow-same-origin` (with `srcdoc`, `allow-same-origin` = the parent BluePrint
// app origin → model-derived HTML would run as first-party script). The rendered
// HTML is safe by construction (V1 constructive renderer) and is injected into
// `srcDoc` VERBATIM — no re-escaping at the card (double-escaping would render
// `<div>` as visible text). The empty sandbox is the runtime containment.
//
// UXV.V3 (#1418) — ADDITIVE annotation chrome. A transparent overlay (a PARENT-
// rendered sibling of the iframe, inside a new position:relative wrapper around
// ONLY the iframe) captures click coordinates at the parent level (C1). The iframe
// element below is UNCHANGED — `sandbox=""` is untouched, no postMessage is used
// (the overlay onClick fires in the React tree). Pins/coords are percentages; the
// server derives the component_region. See AnnotationOverlay / AnnotationList.

export interface ScreenOption {
  screenId: string;
  screenName: string;
}

interface RenderResponse {
  html: string;
  screenId: string;
  renderSchemaVersion: number;
}

interface Props {
  artefactId: string;
  screens: ScreenOption[];
  // UXV.V4 (#1419) — when true, render the prototype "flow" chrome: Prev/Next
  // controls + a position label that sequence through `screens` in array order.
  // The iframe / sandbox / render-fetch path is UNCHANGED in either mode.
  flow?: boolean;
}

export default function ScreenPreviewCard({ artefactId, screens, flow = false }: Props) {
  const [selectedScreenId, setSelectedScreenId] = useState('');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // UXV.V3 annotation state.
  const [annotationMode, setAnnotationMode] = useState(false);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [pendingPin, setPendingPin] = useState<{ xPct: number; yPct: number } | null>(null);
  const [draftComment, setDraftComment] = useState('');
  const [saving, setSaving] = useState(false);

  // DERIVED effective selection (no setState-in-effect): the chosen screen if it
  // is still in the list, else the first. This handles a screen list that changes
  // (e.g. a new artefact version drops the selected screen) without a resync
  // effect (R2); `key={artefactId}` on the mount additionally remounts for a
  // different artefact.
  const effectiveScreenId = screens.some((s) => s.screenId === selectedScreenId)
    ? selectedScreenId
    : (screens[0]?.screenId ?? '');

  // UXV.V4 — prototype flow navigation. Index of the effective screen in the
  // array; goToOffset wraps (last → first, first → last) and clears any pending
  // pin/draft (the same reset the dropdown onChange does). setState fires only in
  // this click handler, never in render/effect.
  const currentIndex = screens.findIndex((s) => s.screenId === effectiveScreenId);
  function goToOffset(delta: number) {
    if (screens.length === 0) return;
    const base = currentIndex >= 0 ? currentIndex : 0;
    const next = (base + delta + screens.length) % screens.length;
    setSelectedScreenId(screens[next].screenId);
    setLoading(true);            // #1459 — raise loading HERE (event handler, lint-legal) so the new
    setPendingPin(null);         // screen's fetch shows the skeleton instead of lingering the old
    setDraftComment('');         // preview; the render effect's .finally clears it. Batches → 1 render.
  }

  useEffect(() => {
    if (!effectiveScreenId) return;
    let cancelled = false;
    // setState ONLY in the async callbacks — never synchronously in the effect
    // body (react-hooks/set-state-in-effect). `loading` inits to true so the
    // first render shows the loading state; the finally clears it. On a screen
    // SWITCH, loading is re-raised by the nav handlers (goToOffset / the selector
    // onChange) — not here — for the same lint reason (#1459).
    apiFetch<RenderResponse>(`/artefacts/${artefactId}/screens/${encodeURIComponent(effectiveScreenId)}/render`)
      .then((res) => { if (!cancelled) { setHtml(res.html); setError(''); } })
      .catch(() => { if (!cancelled) { setError('Preview not available'); setHtml(''); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [artefactId, effectiveScreenId]);

  // UXV.V3 — load stored annotations for the current screen. Resetting the pending
  // pin / draft happens here (in the async callback, not the effect body) so a
  // screen switch clears any in-progress pin. Defensive: tolerate a non-array body.
  useEffect(() => {
    if (!effectiveScreenId) return;
    let cancelled = false;
    apiFetch<{ annotations: Annotation[] }>(`/artefacts/${artefactId}/screens/${encodeURIComponent(effectiveScreenId)}/annotations`)
      .then((res) => { if (!cancelled) { setAnnotations(Array.isArray(res.annotations) ? res.annotations : []); setPendingPin(null); setDraftComment(''); } })
      .catch(() => { if (!cancelled) { setAnnotations([]); setPendingPin(null); setDraftComment(''); } });
    return () => { cancelled = true; };
  }, [artefactId, effectiveScreenId]);

  function toggleAnnotationMode() {
    const next = !annotationMode;
    setAnnotationMode(next);
    if (!next) { setPendingPin(null); setDraftComment(''); } // leaving annotate → drop any pending pin
  }

  function handlePlace(xPct: number, yPct: number) {
    setPendingPin({ xPct, yPct });
    setDraftComment('');
  }

  function cancelPending() {
    setPendingPin(null);
    setDraftComment('');
  }

  function submitAnnotation() {
    const comment = draftComment.trim();
    if (!pendingPin || !comment) return;
    setSaving(true);
    apiFetch<Annotation>(
      `/artefacts/${artefactId}/screens/${encodeURIComponent(effectiveScreenId)}/annotations`,
      { method: 'POST', body: JSON.stringify({ xPct: pendingPin.xPct, yPct: pendingPin.yPct, comment }) },
    )
      .then((created) => { setAnnotations((prev) => [...prev, created]); setPendingPin(null); setDraftComment(''); })
      .catch(() => { /* keep the pending pin so the reviewer can retry */ })
      .finally(() => setSaving(false));
  }

  if (screens.length === 0) {
    return (
      <div className="screen-preview-card" style={{ padding: 24, color: 'var(--text-secondary)' }}>
        No screens yet — generate screen specifications to see a preview.
      </div>
    );
  }

  return (
    <div className="screen-preview-card">
      <div className="screen-preview-card__header" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <label htmlFor="screen-preview-select" style={{ fontSize: 13, fontWeight: 600 }}>Screen</label>
        <select
          id="screen-preview-select"
          value={effectiveScreenId}
          onChange={(e) => { setSelectedScreenId(e.target.value); setLoading(true); setPendingPin(null); setDraftComment(''); }}
        >
          {screens.map((s) => (
            <option key={s.screenId} value={s.screenId}>{s.screenName}</option>
          ))}
        </select>
        {flow && (
          <>
            <button
              type="button"
              data-testid="prototype-prev"
              onClick={() => goToOffset(-1)}
              disabled={screens.length < 2}
              style={{ fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: screens.length < 2 ? 'default' : 'pointer' }}
            >
              ← Prev
            </button>
            <span data-testid="prototype-position" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Screen {currentIndex >= 0 ? currentIndex + 1 : 1} of {screens.length}
            </span>
            <button
              type="button"
              data-testid="prototype-next"
              onClick={() => goToOffset(1)}
              disabled={screens.length < 2}
              style={{ fontSize: 13, fontWeight: 600, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: screens.length < 2 ? 'default' : 'pointer' }}
            >
              Next →
            </button>
          </>
        )}
        <button
          type="button"
          data-testid="annotate-toggle"
          aria-pressed={annotationMode}
          onClick={toggleAnnotationMode}
          style={{
            marginLeft: 'auto', fontSize: 13, fontWeight: 600, padding: '6px 14px',
            borderRadius: 6, cursor: 'pointer',
            border: annotationMode ? 'none' : '1px solid var(--border)',
            background: annotationMode ? 'var(--primary, #7c3aed)' : 'transparent',
            color: annotationMode ? '#fff' : 'var(--text)',
          }}
        >
          {annotationMode ? 'Done annotating' : 'Annotate'}
        </button>
      </div>

      {loading && (
        <div className="screen-preview-card__loading" style={{ padding: 24, color: 'var(--text-secondary)' }}>
          Loading preview…
        </div>
      )}
      {!loading && error && (
        <div role="alert" className="screen-preview-card__error" style={{ padding: 24, color: 'var(--error)' }}>
          {error}
        </div>
      )}
      {!loading && !error && html && (
        // UXV.V3: position:relative wrapper around ONLY the iframe block, so the overlay's inset:0
        // aligns to the iframe (NOT the whole card — the header/selector must stay clickable).
        <div style={{ position: 'relative' }}>
          <iframe
            title="Screen preview"
            // H1: empty sandbox (all restrictions); never allow-scripts/allow-same-origin.
            sandbox=""
            referrerPolicy="no-referrer"
            // Verbatim — the server HTML is already safe (V1); no re-escaping here.
            srcDoc={html}
            className="screen-preview-card__frame"
            style={{ width: '100%', height: 600, border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
          />
          <AnnotationOverlay active={annotationMode} annotations={annotations} onPlace={handlePlace} />
          {pendingPin && (
            <div
              data-testid="pending-pin"
              aria-hidden
              style={{
                position: 'absolute', left: `${pendingPin.xPct * 100}%`, top: `${pendingPin.yPct * 100}%`,
                transform: 'translate(-50%, -50%)', pointerEvents: 'none', width: 22, height: 22,
                borderRadius: '50%', background: '#fff', border: '2px dashed var(--primary, #7c3aed)',
              }}
            />
          )}
        </div>
      )}

      {pendingPin && (
        <div className="screen-preview-card__pending" style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
          <label htmlFor="annotation-comment" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            New annotation
          </label>
          <textarea
            id="annotation-comment"
            data-testid="annotation-comment"
            value={draftComment}
            onChange={(e) => setDraftComment(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Describe the issue at this point…"
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" data-testid="annotation-save" disabled={saving || !draftComment.trim()} onClick={submitAnnotation}>
              {saving ? 'Saving…' : 'Save annotation'}
            </button>
            <button type="button" data-testid="annotation-cancel" disabled={saving} onClick={cancelPending}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <AnnotationList annotations={annotations} />
    </div>
  );
}
